import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Bot, Loader2, Info, AlertCircle, CheckCircle2 } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { useToast } from "@/hooks/use-toast";
import {
  createAgent,
  listAgentVersions,
  createAgentVersion,
  updateAgentVersion,
  checkServiceExists,
  type Agent,
  type PromptStep,
  type AgentType,
} from "@/services/agentsService";

interface AgentFormProps {
  onBack: () => void;
  onSave: () => void;
  editingAgent: Agent | null;
  agentTypes: AgentType[];
}

interface FormData {
  name: string;
  service: string;
  description: string;
  type_id: number;
  versionDescription: string;
  prompts: PromptStep[];
  originalStepsCount: number;
  lastVersionId: string;
  isEditingDraft: boolean;
}

export const AgentForm = ({
  onBack,
  onSave,
  editingAgent,
  agentTypes,
}: AgentFormProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingService, setIsCheckingService] = useState(false);
  const [serviceError, setServiceError] = useState<string>("");
  const [serviceValid, setServiceValid] = useState<boolean>(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalFormData, setOriginalFormData] = useState<FormData | null>(null);
  const [formData, setFormData] = useState<FormData>({
    name: "",
    service: "",
    description: "",
    type_id: 1,
    versionDescription: "",
    prompts: [] as PromptStep[],
    originalStepsCount: 0,
    lastVersionId: "",
    isEditingDraft: false,
  });
  const { toast } = useToast();

  useEffect(() => {
    if (editingAgent) {
      loadAgentData();
      setServiceValid(true);
    }
  }, [editingAgent]);

  const validateServiceFormat = (service: string): string | null => {
    if (!service) {
      return "O serviço é obrigatório";
    }

    if (service.length < 3) {
      return "O serviço deve ter no mínimo 3 caracteres";
    }

    if (service.length > 50) {
      return "O serviço deve ter no máximo 50 caracteres";
    }

    const validPattern = /^[a-z0-9][a-z0-9-_]*[a-z0-9]$/;
    if (!validPattern.test(service)) {
      return "Use apenas letras minúsculas, números, hífen (-) e underscore (_). Deve iniciar e terminar com letra ou número";
    }

    if (/\s/.test(service)) {
      return "O serviço não pode conter espaços";
    }

    if (/[A-Z]/.test(service)) {
      return "O serviço não pode conter letras maiúsculas";
    }

    if (/[àáâãäåèéêëìíîïòóôõöùúûüýÿñç]/i.test(service)) {
      return "O serviço não pode conter acentos ou caracteres especiais";
    }

    return null;
  };

  const checkServiceUniqueness = useCallback(
    async (service: string) => {
      if (!service || editingAgent) return;

      const formatError = validateServiceFormat(service);
      if (formatError) {
        setServiceError(formatError);
        setServiceValid(false);
        return;
      }

      setIsCheckingService(true);
      setServiceError("");

      try {
        const response = await checkServiceExists(service, editingAgent?.id);

        if (response.success && response.data) {
          if (response.data.exists) {
            setServiceError("Este serviço já está em uso por outro agente");
            setServiceValid(false);
          } else {
            setServiceError("");
            setServiceValid(true);
          }
        }
      } catch (error) {
      } finally {
        setIsCheckingService(false);
      }
    },
    [editingAgent]
  );

  useEffect(() => {
    if (!editingAgent && formData.service) {
      const timeoutId = setTimeout(() => {
        checkServiceUniqueness(formData.service);
      }, 500);

      return () => clearTimeout(timeoutId);
    }
  }, [formData.service, checkServiceUniqueness, editingAgent]);

  const loadAgentData = async () => {
    if (!editingAgent) return;

    setIsLoading(true);
    try {
      const versionsResponse = await listAgentVersions(editingAgent.id);
      const versions = versionsResponse.data || [];

      const draftVersion = versions.find((v) => !v.is_published);
      const versionToEdit = draftVersion || versions[0];

      let stepsToLoad: PromptStep[] = [];
      let originalCount = 0;
      let versionId = "";
      let isEditingDraft = false;

      if (versionToEdit) {
        stepsToLoad = versionToEdit.steps || [];
        versionId = versionToEdit.id;
        isEditingDraft = !versionToEdit.is_published;

        if (!isEditingDraft) {
          const lastPublishedVersion = versions.find((v) => v.is_published);
          originalCount = lastPublishedVersion?.steps?.length || 0;
        } else {
          originalCount = stepsToLoad.length;
        }
      }

      const initialData = {
        name: editingAgent.name,
        service: editingAgent.service,
        description: editingAgent.description,
        type_id: editingAgent.type_id,
        versionDescription: "",
        prompts: stepsToLoad,
        originalStepsCount: originalCount,
        lastVersionId: versionId,
        isEditingDraft,
      };

      setFormData(initialData);
      setOriginalFormData(initialData);
      setHasChanges(false);
    } finally {
      setIsLoading(false);
    }
  };

  const checkForChanges = useCallback(() => {
    if (!editingAgent || !originalFormData) {
      setHasChanges(false);
      return;
    }

    const promptsChanged = JSON.stringify(formData.prompts) !== JSON.stringify(originalFormData.prompts);

    setHasChanges(promptsChanged);
  }, [editingAgent, originalFormData, formData.prompts]);

  useEffect(() => {
    if (editingAgent) {
      checkForChanges();
    }
  }, [editingAgent, formData.prompts, checkForChanges]);

  const compareVersions = (v1: string, v2: string): number => {
    const parts1 = v1.split(".").map(Number);
    const parts2 = v2.split(".").map(Number);

    for (let i = 0; i < 3; i++) {
      const num1 = parts1[i] || 0;
      const num2 = parts2[i] || 0;

      if (num1 > num2) return 1;
      if (num1 < num2) return -1;
    }

    return 0;
  };

  const calculateChangeType = ():
    | "initial"
    | "content_change"
    | "steps_change" => {
    if (!editingAgent) return "initial";

    const originalCount = formData.originalStepsCount;
    const newCount = formData.prompts.length;

    if (originalCount !== newCount) {
      return "steps_change";
    }

    return "content_change";
  };

  const handleServiceChange = (value: string) => {
    const sanitized = value.toLowerCase().replace(/\s/g, "-");
    setFormData({ ...formData, service: sanitized });

    if (!editingAgent) {
      const formatError = validateServiceFormat(sanitized);
      if (formatError) {
        setServiceError(formatError);
        setServiceValid(false);
      }
    }
  };

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.service.trim()) {
      toast({
        title: "Erro",
        description: "Nome e serviço são obrigatórios.",
        variant: "destructive",
      });
      return;
    }

    if (!editingAgent) {
      const formatError = validateServiceFormat(formData.service);
      if (formatError) {
        toast({
          title: "Erro no campo Serviço",
          description: formatError,
          variant: "destructive",
        });
        return;
      }

      if (serviceError || !serviceValid) {
        toast({
          title: "Erro no campo Serviço",
          description: serviceError || "Aguarde a validação do serviço",
          variant: "destructive",
        });
        return;
      }
    }

    if (editingAgent && hasChanges && !formData.versionDescription.trim()) {
      toast({
        title: "Descrição obrigatória",
        description: "Por favor, descreva as alterações realizadas antes de salvar.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      if (editingAgent) {
        const changeType = calculateChangeType();

        if (formData.isEditingDraft && formData.lastVersionId) {
          const response = await updateAgentVersion(formData.lastVersionId, {
            description: formData.versionDescription,
            steps: formData.prompts,
            change_type: changeType,
          });

          if (response.success) {
            toast({
              title: "Sucesso",
              description: "Versão rascunho atualizada com sucesso",
            });
            onSave();
          } else {
            toast({
              title: "Erro",
              description:
                response.error?.message || "Erro ao atualizar versão",
              variant: "destructive",
            });
          }
        } else {
          const versionsResponse = await listAgentVersions(editingAgent.id);
          const allVersions = versionsResponse.data || [];
          const existingDraft = allVersions.find((v) => !v.is_published);

          const lastPublishedVersion = allVersions.find((v) => v.is_published);
          const lastPublishedStepsCount =
            lastPublishedVersion?.steps?.length || 0;

          const actualChangeType =
            lastPublishedStepsCount !== formData.prompts.length
              ? "steps_change"
              : "content_change";

          let shouldUpdateExistingDraft = false;
          if (existingDraft && lastPublishedVersion) {
            const draftIsNewer =
              compareVersions(
                existingDraft.version,
                lastPublishedVersion.version
              ) > 0;
            shouldUpdateExistingDraft = draftIsNewer;

            if (!draftIsNewer) {
            }
          } else if (existingDraft && !lastPublishedVersion) {
            shouldUpdateExistingDraft = true;
          }

          if (shouldUpdateExistingDraft && existingDraft) {
            const response = await updateAgentVersion(existingDraft.id, {
              description: formData.versionDescription,
              steps: formData.prompts,
              change_type: actualChangeType,
            });

            if (response.success) {
              toast({
                title: "Sucesso",
                description: "Rascunho existente atualizado com as alterações",
              });
              onSave();
            } else {
              toast({
                title: "Erro",
                description:
                  response.error?.message || "Erro ao atualizar rascunho",
                variant: "destructive",
              });
            }
          } else {
            const response = await createAgentVersion(editingAgent.id, {
              description: formData.versionDescription,
              is_published: false,
              steps: formData.prompts,
              change_type: actualChangeType,
            });

            if (response.success) {
              toast({
                title: "Sucesso",
                description: `Nova versão criada com sucesso (${
                  actualChangeType === "steps_change" ? "minor" : "patch"
                })`,
              });
              onSave();
            } else {
              toast({
                title: "Erro",
                description: response.error?.message || "Erro ao criar versão",
                variant: "destructive",
              });
            }
          }
        }
      } else {
        const response = await createAgent({
          name: formData.name,
          service: formData.service,
          description: formData.description,
          type_id: formData.type_id,
          is_active: true,
        });

        if (response.success && response.data) {
          const versionResponse = await createAgentVersion(response.data.id, {
            description: formData.versionDescription || "Versão inicial",
            is_published: false,
            steps: formData.prompts,
            change_type: "initial",
          });

          if (versionResponse.success) {
            toast({
              title: "Sucesso",
              description: `Agente criado com versão 1.0.0. Publique a versão para ativá-la.`,
            });
          } else {
            toast({
              title: "Aviso",
              description:
                "Agente criado, mas houve erro ao criar versão: " +
                (versionResponse.error?.message || "Erro desconhecido"),
              variant: "destructive",
            });
          }

          onSave();
        } else {
          toast({
            title: "Erro",
            description: response.error?.message || "Erro ao criar agente",
            variant: "destructive",
          });
        }
      }
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao conectar com o servidor",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const addPromptStep = () => {
    const newStep: PromptStep = {
      name: "",
      prompt: "",
      order_num: formData.prompts.length,
    };
    setFormData({
      ...formData,
      prompts: [...formData.prompts, newStep],
    });
  };

  const updatePromptStep = (
    index: number,
    field: string,
    value: string | number
  ) => {
    setFormData({
      ...formData,
      prompts: formData.prompts.map((step, i) =>
        i === index ? { ...step, [field]: value } : step
      ),
    });
  };

  const removePromptStep = (index: number) => {
    setFormData({
      ...formData,
      prompts: formData.prompts.filter((_, i) => i !== index),
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted">
      <PageHeader
        icon={Bot}
        title={editingAgent ? `Editar - ${editingAgent.name}` : "Novo Agente"}
        description={
          editingAgent ? (
            formData.isEditingDraft ? (
              <span>
                Editando versão rascunho. As alterações atualizarão esta
                versão.
              </span>
            ) : (
              <span>
                Editando versão publicada. Ao salvar, o sistema irá:
                atualizar rascunho existente ou criar nova versão
              </span>
            )
          ) : (
            "Configure o agente e seus prompts de processamento"
          )
        }
        onBack={onBack}
        actions={
          <>
            <Button variant="outline" onClick={onBack}>
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              className="bg-gradient-primary hover:opacity-90"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Salvando...
                </>
              ) : editingAgent ? (
                "Salvar Alterações"
              ) : (
                "Criar Agente"
              )}
            </Button>
          </>
        }
      />

      <main className="container mx-auto p-6">
        <div className="max-w-5xl mx-auto space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Informações Básicas</CardTitle>
              <CardDescription>
                Configure os dados principais do agente
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="name">Nome do Agente</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    placeholder="Ex: Agente de Leitura de Imagem"
                    disabled={!!editingAgent}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="service" className="flex items-center gap-2">
                    Serviço
                    <div className="group relative">
                      <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                      <div className="hidden group-hover:block absolute left-0 top-6 z-50 w-72 p-3 bg-popover text-popover-foreground rounded-md shadow-md border text-sm">
                        <p className="font-semibold mb-2">Regras para o nome do serviço:</p>
                        <ul className="list-disc list-inside space-y-1 text-xs">
                          <li>Apenas letras minúsculas (a-z)</li>
                          <li>Números permitidos (0-9)</li>
                          <li>Hífen (-) e underscore (_) permitidos</li>
                          <li>Sem espaços ou acentos</li>
                          <li>Deve começar e terminar com letra ou número</li>
                          <li>Entre 3 e 50 caracteres</li>
                          <li>Nome único no sistema</li>
                        </ul>
                        <p className="mt-2 text-xs text-muted-foreground">
                          Ex: image-analysis, text-processor, data_analyzer
                        </p>
                      </div>
                    </div>
                  </Label>
                  <div className="relative">
                    <Input
                      id="service"
                      value={formData.service}
                      onChange={(e) => handleServiceChange(e.target.value)}
                      placeholder="Ex: image-analysis"
                      disabled={!!editingAgent}
                      className={
                        !editingAgent && formData.service
                          ? serviceError
                            ? "border-destructive focus-visible:ring-destructive pr-10"
                            : serviceValid
                            ? "border-green-500 focus-visible:ring-green-500 pr-10"
                            : ""
                          : ""
                      }
                    />
                    {!editingAgent && formData.service && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        {isCheckingService ? (
                          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                        ) : serviceError ? (
                          <AlertCircle className="h-4 w-4 text-destructive" />
                        ) : serviceValid ? (
                          <CheckCircle2 className="h-4 w-4 text-green-500" />
                        ) : null}
                      </div>
                    )}
                  </div>
                  {!editingAgent && serviceError && (
                    <p className="text-sm text-destructive flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      {serviceError}
                    </p>
                  )}
                  {!editingAgent && serviceValid && !serviceError && (
                    <p className="text-sm text-green-600 flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3" />
                      Serviço disponível
                    </p>
                  )}
                  {editingAgent && (
                    <p className="text-xs text-muted-foreground">
                      O nome do serviço não pode ser alterado
                    </p>
                  )}
                </div>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="type_id">Tipo do Agente</Label>
                <Select
                  value={formData.type_id.toString()}
                  onValueChange={(value) =>
                    setFormData({ ...formData, type_id: parseInt(value) })
                  }
                  disabled={!!editingAgent}
                >
                  <SelectTrigger id="type_id">
                    <SelectValue placeholder="Selecione o tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    {agentTypes.map((type) => (
                      <SelectItem key={type.id} value={type.id.toString()}>
                        {type.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="description">Descrição do Agente</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder="Descreva a função deste agente..."
                  rows={3}
                  disabled={!!editingAgent}
                />
              </div>
            </CardContent>
          </Card>

          {editingAgent && (
            <Card className="border-2 border-primary/20">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Informações da Versão</CardTitle>
                    <CardDescription>
                      Descreva as alterações realizadas nesta edição
                    </CardDescription>
                  </div>
                  {formData.isEditingDraft && (
                    <Badge variant="outline" className="bg-blue-50">
                      Editando Rascunho
                    </Badge>
                  )}
                  {!formData.isEditingDraft && (
                    <Badge variant="outline" className="bg-amber-50">
                      Editando Publicada
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-2">
                  <Label htmlFor="versionDescription" className="flex items-center gap-2">
                    Descrição das alterações
                    {hasChanges && (
                      <span className="text-xs text-destructive">(obrigatório)</span>
                    )}
                  </Label>
                  <Textarea
                    id="versionDescription"
                    value={formData.versionDescription}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        versionDescription: e.target.value,
                      })
                    }
                    placeholder="Ex: Ajustado prompt de análise inicial, Adicionado validação de entrada"
                    rows={3}
                    className={hasChanges && !formData.versionDescription ? "border-amber-500" : ""}
                  />
                  {hasChanges && !formData.versionDescription && (
                    <p className="text-sm text-amber-600 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      Descreva as mudanças realizadas para criar o histórico de alterações
                    </p>
                  )}
                  {!hasChanges && (
                    <p className="text-sm text-muted-foreground">
                      Nenhuma alteração detectada. Modifique os prompts para criar uma nova versão.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Prompts do Agente</CardTitle>
                  <CardDescription>
                    Configure os prompts de processamento em etapas
                  </CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={addPromptStep}>
                  <Plus className="h-3 w-3 mr-1" />
                  Adicionar Prompt
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {formData.prompts.map((prompt, index) => (
                  <Card key={index} className="p-4 bg-muted/30">
                    <div className="flex items-center justify-between mb-3">
                      <Badge variant="outline">Etapa {index + 1}</Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removePromptStep(index)}
                        className="h-6 w-6 p-0 hover:bg-destructive/10 hover:text-destructive"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                    <div className="grid gap-3">
                      <div className="grid gap-2">
                        <Label>Nome da Etapa</Label>
                        <Input
                          value={prompt.name}
                          onChange={(e) =>
                            updatePromptStep(index, "name", e.target.value)
                          }
                          placeholder="Ex: Análise Inicial"
                        />
                      </div>
                      <div className="grid gap-2">
                        <Label>Prompt</Label>
                        <Textarea
                          value={prompt.prompt}
                          onChange={(e) =>
                            updatePromptStep(index, "prompt", e.target.value)
                          }
                          placeholder="Digite o prompt para esta etapa..."
                          rows={4}
                        />
                      </div>
                    </div>
                  </Card>
                ))}

                {formData.prompts.length === 0 && (
                  <div className="text-center py-12 text-muted-foreground border-2 border-dashed rounded-lg">
                    <Bot className="h-16 w-16 mx-auto mb-4 opacity-50" />
                    <p className="font-medium">Nenhum prompt configurado</p>
                    <p className="text-sm mt-1">
                      Clique em "Adicionar Prompt" para começar
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

